import { useCallback, useEffect, useRef } from 'react';

interface Options {
  delayMs: number;
  /** When true, the countdown is stopped (e.g. while a submit is in flight). */
  paused?: boolean;
}

/**
 * Fires `onIdle` after `delayMs` of inactivity. Call `reset()` on any activity
 * to restart the countdown; `paused` stops it; it clears on unmount.
 * Side-effect only — owns a single timer.
 */
export const useIdleTimeout = (
  onIdle: () => void,
  { delayMs, paused = false }: Options,
): { reset: () => void } => {
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const onIdleRef = useRef(onIdle);
  onIdleRef.current = onIdle;

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = undefined;
    }
  }, []);

  const reset = useCallback(() => {
    clear();
    if (paused) return;
    timer.current = setTimeout(() => onIdleRef.current(), delayMs);
  }, [clear, paused, delayMs]);

  useEffect(() => {
    reset();
    return clear;
  }, [reset, clear]);

  return { reset };
};
